import { Stack } from "@mui/material";
import {
  addDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";

import { CalendarSlot, Meeting } from "./../../types";

import {
  EditMeetingModal,
  MeetingInfoModal,
  ScheduleMeetingModal,
} from "../meeting-management";
import CalendarToolbar from "./toolbar/calendar-toolbar";
import AgendaView from "./agenda-view/agenda-view";
import WeeklyView from "./weekly-view/weekly-view";
import MonthlyView from "./monthly-view/monthly-view";
import CalendarSidebar from "./sidebar/calendar-sidebar";

import React, { useMemo, useState } from "react";
import { useListMeeting, useListPatients } from "../../store"
import { useEventCalendarValues } from "../../routes/calendar/calendar-context"
import {
  hasMeetingOnHiddenDay,
  hasMeetingOutsideFocusHours,
} from "./sidebar/calendar-warning-utils"

type CalendarView = "month" | "week" | "agenda";

type TimeSlot = {
  start: Date;
  end: Date;
};

export const getMeetingListRange = (
  date: Date,
  view: CalendarView,
): { startRange: Date; endRange: Date } => {
  if (view === "week") {
    const startRange = startOfWeek(date, { weekStartsOn: 0 });
    const endRange = endOfWeek(date, { weekStartsOn: 0 });

    return {
      startRange: startOfDay(startRange),
      endRange: endOfDay(endRange),
    };
  }

  if (view === "agenda") {
    const startRange = startOfDay(date);
    const endRange = addDays(startRange, 29);

    return {
      startRange,
      endRange: endOfDay(endRange),
    };
  }

  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);

  return {
    startRange: startOfWeek(monthStart, { weekStartsOn: 0 }),
    endRange: endOfDay(endOfWeek(monthEnd, { weekStartsOn: 0 })),
  };
};

const getDefaultView = (): CalendarView => {

  // Get stored view preference
  const storedView = localStorage.getItem("lastView");
  if (!storedView) {
    return "month"
  }

  return storedView as CalendarView
}

const getDefaultSidebarOpen = () => {
  const storedValue = localStorage.getItem("calendarSidebarOpen");

  if (storedValue === null) {
    return false;
  }

  return storedValue === "true";
};

const EventCalendar: React.FC = () => {
  const {
    visibleMeetingTypes,
    visiblePatientIds,
    hasInteractedWithPatientFilter,
    visibleWeekDays,
    focusStartHour,
    focusEndHour,
  } = useEventCalendarValues()

  const [currentMeeting, setCurrentMeeting] = useState<Meeting | null>(null)
  const [currentSlot, setCurrentSlot] = useState<TimeSlot | null>(null)
  const [view, setView] = useState<CalendarView>(getDefaultView())
  const [date, setDate] = useState(new Date())
  const [isSidebarOpen, setIsSidebarOpen] = useState(getDefaultSidebarOpen())

  const handleViewChange = (newView: CalendarView) => {
    setView(newView);
    localStorage.setItem("lastView", newView)
  };

  const handleSidebarToggle = () => {
    setIsSidebarOpen((currentValue) => {
      const nextValue = !currentValue;

      localStorage.setItem("calendarSidebarOpen", nextValue ? "true" : "false");

      return nextValue;
    });
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
    localStorage.setItem("calendarSidebarOpen", "false");
  };

  const [scheduleMeetingModalOpen, setScheduleMeetingModalOpen] = React.useState(false);
  const [editMeetingModalOpen, setEditMeetingModalOpen] = React.useState(false);

  const handleScheduleMeetingModalClose = () => {
    setCurrentSlot(null);
    setScheduleMeetingModalOpen(false);
  };

  const handleScheduleMeetingModalOpen = () => {
    setCurrentSlot(null);
    setScheduleMeetingModalOpen(true);
  };

  const handleMeetingEditModalOpen = (meeting: Meeting | null) => {
    setCurrentMeeting(meeting);
    setEditMeetingModalOpen(true);
  }

  const handleMeetingEditModalClose = () => {
    setEditMeetingModalOpen(false);
    setCurrentMeeting(null);
  };

  const handleWeeklyTimeslotSelect = (start: Date, end: Date) => {
    setCurrentSlot({
      start,
      end,
    });
    setScheduleMeetingModalOpen(true);
  };

  const { startRange, endRange } = useMemo(
    () => getMeetingListRange(date, view),
    [date, view],
  );

  const { data: unfilteredMeetingList = [], isLoading: meetingsLoading } = useListMeeting(
    startRange,
    endRange,
  );
  const { data: patients = [] } = useListPatients();

  const shouldShowMeetingType = (meeting: CalendarSlot): boolean => {
    return visibleMeetingTypes.includes(meeting.meetingType)
  };

  const shouldShowPatient = (meeting: CalendarSlot): boolean => {
    const patientId = meeting.patientId ?? "";

    return !hasInteractedWithPatientFilter || visiblePatientIds.includes(patientId)
  };

  const typeFilteredMeetings = useMemo(() => {
    return unfilteredMeetingList.filter((meeting) => {
      return shouldShowMeetingType(meeting);
    })
  }, [unfilteredMeetingList, visibleMeetingTypes])

  const availablePatients = useMemo(() => {
    const patientLabelById = patients.reduce<Record<string, string>>((acc, patient) => {
      acc[patient.patientId] = patient.displayId;
      return acc;
    }, {});

    return Array.from(
      new Set(
        [
          ...typeFilteredMeetings
            .map((meeting) => meeting.patientId)
            .filter((patientId): patientId is string => typeof patientId === "string" && patientId.length > 0),
          ...visiblePatientIds,
        ],
      ),
    )
      .map((patientId) => ({
        patientId,
        label: patientLabelById[patientId] || patientId,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [typeFilteredMeetings, patients, visiblePatientIds])

  const meetingList = useMemo(() => {
    return typeFilteredMeetings.filter((meeting) => shouldShowPatient(meeting))
  }, [typeFilteredMeetings, visiblePatientIds])

  const hasHiddenDayMeeting = useMemo(
    () => hasMeetingOnHiddenDay(unfilteredMeetingList, visibleWeekDays),
    [unfilteredMeetingList, visibleWeekDays],
  )

  const hasHiddenFocusTimeMeeting = useMemo(
    () => hasMeetingOutsideFocusHours(unfilteredMeetingList, focusStartHour, focusEndHour),
    [unfilteredMeetingList, focusStartHour, focusEndHour],
  )

  return (
    <Stack
      direction="column"
      spacing={2}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        overflow: "visible",
      }}
    >
      <CalendarToolbar
        date={date}
        setDate={setDate}
        addButtonClick={handleScheduleMeetingModalOpen}
        setView={handleViewChange}
        view={view}
        isSidebarOpen={isSidebarOpen}
        showSidebarWarning={hasHiddenDayMeeting || hasHiddenFocusTimeMeeting}
        onToggleSidebar={handleSidebarToggle}
      />

      <ScheduleMeetingModal
        open={scheduleMeetingModalOpen}
        startTime={currentSlot?.start || undefined}
        handleClose={handleScheduleMeetingModalClose}
      />
      {currentMeeting && (
        <>
      <MeetingInfoModal
        open={currentMeeting != null}
        handleClose={() => setCurrentMeeting(null)}
        meetingId={currentMeeting.meetingId}
        onEditMeeting={() => handleMeetingEditModalOpen(currentMeeting || null)}
      />
      <EditMeetingModal
        open={editMeetingModalOpen}
        handleClose={handleMeetingEditModalClose}
        meetingId={currentMeeting.meetingId}
      />
      </>
      )}
      <Stack
        direction="row"
        spacing={view == "agenda" && isSidebarOpen ? 2: 0}
        sx={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflow: "visible",
          alignItems: "stretch",
        }}
      >
        <Stack
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {view === "agenda" ? (
            <AgendaView
              meetingList={meetingList}
              date={date}
              visibleWeekDays={visibleWeekDays}
            />
          ) : view === "week" ? (
            <WeeklyView
              meetingList={meetingList}
              date={date}
              visibleWeekDays={visibleWeekDays}
              startTime={focusStartHour}
              endTime={focusEndHour}
              isLoading={meetingsLoading}
              onSelectTimeslot={handleWeeklyTimeslotSelect}
            />
          ) : (
            <MonthlyView
              meetingList={meetingList}
              date={date}
              visibleWeekDays={visibleWeekDays}
              isLoading={meetingsLoading}
              onSelectTimeslot={handleWeeklyTimeslotSelect}
            />
          )}
        </Stack>
        <Stack
          sx={{
            width: isSidebarOpen ? "clamp(260px, 24vw, 320px)" : 0,
            minWidth: isSidebarOpen ? "clamp(260px, 24vw, 320px)" : 0,
            flexShrink: 0,
            minHeight: 0,
            boxSizing: "border-box",
            pb: 1,
            overflow: "visible",
            opacity: isSidebarOpen ? 1 : 0,
            zIndex: 1,
            transition: "width 180ms ease, min-width 180ms ease, opacity 180ms ease",
          }}
        >
          {isSidebarOpen && (
            <CalendarSidebar
              visibleMeetingCount={meetingList.length}
              totalMeetingCount={unfilteredMeetingList.length}
              availablePatients={availablePatients}
              hasHiddenDayMeeting={hasHiddenDayMeeting}
              hasHiddenFocusTimeMeeting={hasHiddenFocusTimeMeeting}
            />
          )}
        </Stack>
      </Stack >
    </Stack >
  )
}

export default EventCalendar;
